import { httpsCallable } from 'firebase/functions'
import type { CompetencyCode, Exam, KnowledgeCode, QuestionType } from '../types'
import { leagueFunctions } from './firebase'

export type LeagueQuestion = {
  id: string
  exam: Exam
  domain: string
  topic: string
  knowledge: KnowledgeCode[]
  competency: CompetencyCode[]
  difficulty: number
  cognitiveLevel: 'M1' | 'M2' | 'M3'
  type: QuestionType
  stem: string
  choices?: Array<{ id: string; text: string }>
  estimatedSeconds: number
  tags: string[]
  variantGroup: string
  prerequisites: KnowledgeCode[]
}

export type DuelSummary = {
  id: string
  inviteCode: string
  visibility: 'public' | 'private'
  status: 'waiting' | 'active' | 'complete' | 'expired'
  topic: string | null
  count: number
  participantCount: number
  opponent: { nickname: string; avatar: string } | null
  score: number | null
  opponentScore: number | null
  ratingChange: number | null
  createdAt: string | null
  expiresAt: string | null
  completedAt: string | null
}

export type DuelDetail = DuelSummary & {
  started: boolean
  questions: LeagueQuestion[]
  answers: Record<string, { correct: boolean; score: number; elapsedSeconds: number }>
}

export type LeagueHome = {
  seasonId: string
  player: { nickname: string; avatar: string; rating: number; xp: number; wins: number; losses: number; draws: number; games: number; rank: number }
  leaderboard: Array<{ rank: number; nickname: string; avatar: string; rating: number; xp: number; isMe: boolean }>
  groups: Array<{ id: string; name: string; inviteCode: string; members: number; seasonPoints: number; isCaptain: boolean }>
  groupLeaderboard: Array<{ rank: number; name: string; members: number; seasonPoints: number }>
  duels: DuelSummary[]
}

type ProfileInput = { nickname: string; avatar: string }

function humanize(error: unknown) {
  const cause = error as { code?: string; message?: string }
  const known: Record<string, string> = {
    'functions/unauthenticated': 'Hãy đăng nhập Google trước khi vào League.',
    'functions/not-found': 'Không tìm thấy lời mời hoặc nhóm này.',
    'functions/resource-exhausted': 'Hiện không thể thực hiện thao tác này.',
    'functions/failed-precondition': 'Trận đấu này đã đổi trạng thái hoặc hết hiệu lực.',
    'functions/deadline-exceeded': 'Bạn đã hết thời gian cho trận này.',
    'functions/already-exists': 'Câu này đã được nộp rồi.',
  }
  return known[cause.code || ''] || cause.message || 'Không thể kết nối Math League. Hãy thử lại.'
}

async function call<T>(name: string, data: Record<string, unknown> = {}) {
  try {
    const result = await httpsCallable<{ action: string; payload: Record<string, unknown> }, T>(leagueFunctions, 'smLeague')({ action: name, payload: data })
    return result.data
  } catch (error) {
    throw new Error(humanize(error))
  }
}

export const leagueRepository = {
  bootstrap(profile: ProfileInput) { return call<LeagueHome>('smBootstrap', profile) },
  createDuel(profile: ProfileInput, visibility: 'public' | 'private', topic: string | null, count: number) { return call<{ id: string; inviteCode: string; status: DuelSummary['status']; visibility: 'public' | 'private' }>('smCreateDuel', { ...profile, visibility, topic, count }) },
  findDuel(profile: ProfileInput, topic: string | null, count: number) { return call<{ id: string; inviteCode: string; status: DuelSummary['status']; matched: boolean }>('smFindDuel', { ...profile, topic, count }) },
  joinDuel(profile: ProfileInput, inviteCode: string) { return call<{ id: string; inviteCode: string; status: DuelSummary['status'] }>('smJoinDuel', { ...profile, inviteCode }) },
  openDuel(duelId: string) { return call<DuelDetail>('smOpenDuel', { duelId }) },
  getDuel(duelId: string) { return call<DuelDetail>('smGetDuel', { duelId }) },
  submitDuelAnswer(duelId: string, questionId: string, answer: string) {
    return call<{ correct: boolean; score: number; elapsedSeconds: number; solution: string; hint1: string; hint2: string; settled: boolean }>('smSubmitDuelAnswer', { duelId, questionId, answer })
  },
  createGroup(profile: ProfileInput, name: string) { return call<LeagueHome['groups'][number]>('smCreateGroup', { ...profile, name }) },
  joinGroup(profile: ProfileInput, inviteCode: string) { return call<LeagueHome['groups'][number]>('smJoinGroup', { ...profile, inviteCode }) },
}
